import { type FC, forwardRef } from 'react';
import classNames from 'classnames/bind';
import { Theme } from '../types/enums';
import { FAIcon, IconStyle } from '../Icons/Icons';
import { NextgenImage } from '../NextgenImage/NextgenImage';
import type { ProfileSectionPropsTypes, ProfileListInterface } from './types/propTypes';
import styles from './ProfileSection.module.scss';

const cx = classNames.bind(styles);
/**
 * @description Profile Section component .
 * Props ->
 * underline: whether to include the underline or not,
 * title: name of the profile section
 * profileData: details of the state
 */

const ProfileSection: FC<ProfileSectionPropsTypes> = forwardRef<HTMLDivElement, ProfileSectionPropsTypes>(
  ({ profileData, underline, title }, ref) => {
    const handleItemClick = (profile: ProfileListInterface) => {
      if (profile.onClick) {
        profile.onClick();
      }
    };
    return (
      <div className={`${styles['profile-container']}`} ref={ref}>
        <div className={`${styles['title-container']}`}>
          <p className={`${styles['profile-title']}`} data-testid="profile-section-title">
            {title}
          </p>
        </div>
        {profileData?.map((profile) => {
          const hasOnClick = profile.onClick !== undefined;
          const CClassname = cx('profile-list-data', { 'with-underline': underline, hoverable: hasOnClick });

          const content = (
            <div
              key={`${profile.label}-${Math.random()}`}
              className={CClassname}
              onClick={hasOnClick ? () => handleItemClick(profile) : undefined}
              data-testid="profile-section-data-list"
              tabIndex={0}
              role={hasOnClick ? 'button' : undefined}
              onKeyDown={
                hasOnClick
                  ? (e) => {
                      if (e.key === 'Enter') {
                        handleItemClick(profile);
                      }
                    }
                  : undefined
              }
            >
              <span className={`${styles['profile-logo']}`}>
                {profile.imageSrc ? (
                  <div className={styles['image-container']} data-testid="profile-imageSrc">
                    <div className={styles['image-content']}>
                      <NextgenImage src={profile.imageSrc} alt="" />
                    </div>
                  </div>
                ) : (
                  profile.iconName && (
                    <div className={styles['icon-container']} data-testid="profile-leftIcon">
                      <FAIcon
                        iconName={profile.iconName}
                        iconStyle={profile.iconType || IconStyle.REGULAR}
                        theme={Theme.BLACK}
                      />
                    </div>
                  )
                )}
                <span className={`${styles['profile-label']}`}>{profile.label}</span>
              </span>
              {profile.badge && (
                <div className={`${styles['badge']}`}>
                  {profile.notificationBadge !== undefined ? (
                    <>
                      {profile.notificationBadge > 0 ? (
                        <div className={`${styles['blue-badge']}`} data-testid="profile-section-test-id">
                          {profile.notificationBadge}
                        </div>
                      ) : (
                        <></>
                      )}
                    </>
                  ) : (
                    <div className={`${styles['error-badge']}`} data-testid="profile-section-error">
                      <FAIcon
                        iconName="exclamation-circle"
                        className={styles['icon']}
                        theme={Theme.RED}
                        data-testid="profile-section-icon"
                      />
                    </div>
                  )}
                </div>
              )}
              {profile.rightIcon ? (
                <div className={`${styles['right-icon']}`} data-testid="profile-section-test-node">
                  {profile.rightIcon}
                </div>
              ) : (
                ''
              )}
            </div>
          );

          const { ContainerElement } = profile;

          return ContainerElement ? <ContainerElement key="container-element">{content}</ContainerElement> : content;
        })}
      </div>
    );
  },
);
ProfileSection.displayName = 'ProfileSection';
export default ProfileSection;
